import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { Observable } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { AppState, RoleMgr, ShowRequest } from "../../../core";
import { ColorScheme } from "../../../core/color-scheme";
import { v4 as uuidv4 } from "uuid";
import {
  activity_status,
  DriveTestPoint,
  PathTestPoint,
  PathTestPointSeq,
} from "../../../core/models";
import { DriveTestDoc } from "../../../core/schema/drive-test-schema";
import { PathTestDoc } from "../../../core/schema/path-test-schema";
import {
  PathTestService,
  PointTestLogDoc,
  PointTestLogService,
  ScoutPointInfo,
} from "../../../core/services";
import { PathViewStateMapData } from "../../path-test.store";
import { LogEntryRow } from "../path-test-point-log/path-test-point-log.component";

export type PathTestPointListVisibleColumns = {
  callCounts?: boolean;
  comments?: boolean;
  environment?: boolean;
  time?: boolean;
  waypoint?: boolean;
  seqStatus?: boolean;
  scoutPtInfo?: boolean;
};

export type PathTestPointPhotoContext = {
  iconCssClass: string;
};
@Component({
  selector: "app-path-test-point-list",
  templateUrl: "./path-test-point-list.component.html",
  styleUrls: ["./path-test-point-list.component.scss"],
})
export class PathTestPointListComponent {
  test: any[] = [];
  @Input() pathTest: PathTestDoc | DriveTestDoc;
  @Input() testPoints: PathTestPoint[] | DriveTestPoint[]; // not same as pathTest.testPoints (may have been sorted/filtered)
  @Input() mapData: PathViewStateMapData;
  @Input() scoutPointInfoByPtId: { [ptId:string]: ScoutPointInfo };
  @Input() initializingScoutInfoByPtId: boolean;
  @Input() selectedTestPoint: PathTestPoint;
  @Input() editFn: (pt: PathTestPoint) => void;
  @Input() editFnDrive: (pt: PathTestPoint) => void;
  @Input() visibleColumns: PathTestPointListVisibleColumns = {
    callCounts: true,
    waypoint: true,
    seqStatus: true,
    scoutPtInfo: true,
  };
  @Output() selectedTestPointChange = new EventEmitter<PathTestPoint>();

  pointColors = ColorScheme.PointColors;
  StatusIds = activity_status.Id;
  tz: string;

  constructor(
    public pathTestService: PathTestService,
    public roleMgr: RoleMgr,
    protected appState: AppState
  ) {
    this.tz = new Date()
      .toLocaleTimeString("en-us", { timeZoneName: "short" })
      .split(" ")[2];
  }

  async onClickAddDrive() {
    this.testPoints.push({
      id: uuidv4(),
      routeId: `${this.pathTest.routeId}`,
      order: this.testPoints.length + 1,
      seqs: [],
      statusId: 1,
    });
    await this.pathTest.atomicPatch({testPoints : this.testPoints})
  }

  waypointSortResolver = (
    pathTestPoint: PathTestPoint,
    fieldName: string
  ): string => {
    const id = pathTestPoint?.id;
    const pathWaypoint = this.mapData?.mapPointsByTestPtId[id]?.pathWaypoint;
    const fieldVal = pathWaypoint && pathWaypoint[fieldName];
    console.log(
      `id: ${id}, mapData: ${this.mapData}, pathWaypoint: ${pathWaypoint}, fieldName: ${fieldName}, fieldVal: ${fieldVal}`
    );
    return `${fieldVal}`;
  };

  latestSeqSortResolver = (
    pathTestPoint: PathTestPoint,
    fieldName: string
  ): number => {
    const seq = pathTestPoint?.seqs?.last();
    const v = seq ? seq[fieldName] : -1;
    if (v === undefined || v === null) {
      return -1;
    }
    return v;
  };

  latestSeqDataSortResolver = (
    pathTestPoint: PathTestPoint,
    fieldName: string
  ): number => {
    const seqData = pathTestPoint?.seqs?.last()?.data;
    const v = seqData ? seqData[fieldName] : -1;
    if (v === undefined || v === null) {
      return -1;
    }
    return v;
  };

  onClickMapPoint(testPoint: PathTestPoint) {
    this.selectedTestPoint = testPoint;
    this.selectedTestPointChange.emit(testPoint);
  }

  onClickPointId(testPoint: PathTestPoint | DriveTestPoint) {
    this.appState.showView.next(
      new ShowRequest("tab", "point", testPoint.pointId, this.pathTest)
    );
  }

  onClickOrder(testPoint: PathTestPoint) {
    this.onClickMapPoint(testPoint);
  }

  savingStatusId: string = null;

  async onTestPointStatusEdited(testPoint: PathTestPoint, event: Event) {
    this.savingStatusId = testPoint?.id;
    try {
    const htmlSelectElement = event.target as HTMLSelectElement;
    const status = parseInt(htmlSelectElement.value);
    console.log(
      `value: ${htmlSelectElement.value}, testPoint: ${testPoint}, event: ${event}, htmlSelectElement: ${htmlSelectElement}`
    );
    const srcDoc = this.pathTest;
    await srcDoc.atomicUpdate((doc) => {
      if (doc.type != "path test") {
        throw new Error(`Invalid state.  This is not a path test (${doc.type}).`);
      }
      const destTestPoint = doc.testPoints.find(tp=>tp.id==testPoint.id);
      destTestPoint.statusId = status;
      return doc;
    });
    } catch ( error: any ) {
      console.error(error);
      this.pathTestService.publishError(error, this);
    } finally {
      this.savingStatusId = null;
    }
  }
}



@Component({
  selector: "app-path-test-point-seq-comments",
  templateUrl: "./path-test-point-seq-comments.component.html",
  styleUrls: ["../path-test-point-log/path-test-point-log.component.scss"],
})
export class PathTestPointSeqCommentsComponent implements OnInit {
  @Input() seq: PathTestPointSeq;
  //@Output('loaded') loaded$ = new EventEmitter<LogEntryRow[]>()
  isLoading: boolean = false;
  logRows$: Observable<LogEntryRow[]>;
  constructor(public logService: PointTestLogService) {}

  ngOnInit(): void {
    if (!this.seq?.id) {
      return;
    }
    this.isLoading = true;
    this.logRows$ = this.logService.rxCollection
      .find()
      .where("taskId")
      .eq(this.seq.id)
      .$.pipe(
        map((logDocs: PointTestLogDoc[]) => {
          logDocs.sort((a, b) => b.timestamp?.localeCompare(a.timestamp));
          const logRows = logDocs.map((logEntry) => {
            let row = new LogEntryRow(logEntry);
            return row;
          });
          this.isLoading = false;
          //this.loaded$.next(logRows)
          return logRows;
        }),
        catchError((error) => {
          this.isLoading = false;
          this.logService.publishError(error, this);
          return [];
        })
      );
  }
}

